import { Grid, GridProps } from '@coolblue-development/freddy';
import type { ReactNode } from 'react';

type ComposeGridProps = {
  isMobile: boolean;
  productCards: Array<React.JSX.Element | undefined>;
  brandBar?: React.JSX.Element;
  uspLineFactory: (seed: number) => React.JSX.Element;
  adPlaceholderFactory: (seed: number) => React.JSX.Element;
  hasSubscription: boolean;
};

export default function composeGrid({
  isMobile,
  productCards,
  brandBar,
  uspLineFactory,
  adPlaceholderFactory,
  hasSubscription
}: ComposeGridProps) {
  const gridElements = [...productCards];

  const POSITIONS = {
    MOBILE: {
      AD: 1,
      USP: 7,
      BRAND_BAR: 6
    },
    DESKTOP: {
      AD: 2,
      USP_START: 3,
      USP_INTERVAL: 7,
      BRAND_BAR: 7
    }
  };
  const MINIMUM_NUMBER_OF_PRODUCT_CARDS_FOR_BOTTOM_AD = 11;

  function insertElement(
    index: number,
    deleteCount: number,
    ...items: (React.JSX.Element | undefined)[]
  ) {
    gridElements.splice(index, deleteCount, ...items);
  }

  function insertAd() {
    if (!hasSubscription && productCards.length > 1) {
      const adIndex = isMobile ? POSITIONS.MOBILE.AD : POSITIONS.DESKTOP.AD;
      insertElement(adIndex, 0, adPlaceholderFactory(0));
    }
  }

  function insertUSP() {
    if (isMobile) {
      insertElement(POSITIONS.MOBILE.USP, 0, uspLineFactory(0));
    } else {
      for (
        let i = POSITIONS.DESKTOP.USP_START;
        i < gridElements.length;
        i += POSITIONS.DESKTOP.USP_INTERVAL
      ) {
        insertElement(i, 0, uspLineFactory(i));
      }
    }
  }

  function insertBrandBar() {
    if (!brandBar) return;
    const index = isMobile ? POSITIONS.MOBILE.BRAND_BAR : POSITIONS.DESKTOP.BRAND_BAR;
    insertElement(index, 0, brandBar);
  }

  function appendAdAtEndIfNeeded() {
    const lastElement = gridElements[gridElements.length - 1];
    if (
      !hasSubscription &&
      productCards.length > MINIMUM_NUMBER_OF_PRODUCT_CARDS_FOR_BOTTOM_AD &&
      !hasKey('ad-placeholder', lastElement)
    ) {
      gridElements.push(adPlaceholderFactory(1));
    }
  }

  insertAd();
  insertUSP();
  insertBrandBar();
  appendAdAtEndIfNeeded();

  if (isMobile) {
    while (isFullWidth(gridElements[gridElements.length - 1])) {
      gridElements.pop();
    }
    return gridElements;
  }

  return createDesktopRows(gridElements);
}

function isFullWidth(element?: JSX.Element) {
  return hasKey('brand-bar', element) || hasKey('usp-line', element);
}

function hasKey(key: string, element?: JSX.Element) {
  return element?.key?.includes(key);
}

function WrapperGrid({ children, ...rest }: { children: ReactNode } & GridProps) {
  return (
    <Grid templateColumns={{ xs: '1fr', lg: 'repeat(3, 1fr)' }} {...rest}>
      {children}
    </Grid>
  );
}

function createDesktopRows(elements: (JSX.Element | undefined)[]) {
  const gridRows: (JSX.Element | undefined)[] = [];

  // Hides the dynamic highlight section if all product cards in the row have empty dynamic highlights.
  const hideEmptyRowSelector = `
    &:not(
      :has(
        .product-grid-dynamic-highlights:not(:empty)
      )
    ) > div > div > :nth-of-type(3)
  `;

  let index = 0;

  while (index < elements.length) {
    const element = elements[index];

    if (isFullWidth(element)) {
      if (index < elements.length - 2) {
        gridRows.push(<WrapperGrid key={index}>{element}</WrapperGrid>);
      }
      index += 1;
      continue;
    }

    const elementsToInsert = elements.slice(index, index + 3).filter(e => !isFullWidth(e));

    gridRows.push(
      <WrapperGrid
        key={index}
        columnGap={{ md: 400, lg: 600 }}
        rowGap={{ xs: 500, lg: 0 }}
        sx={{ [hideEmptyRowSelector]: { display: 'none' } }}
      >
        {elementsToInsert}
      </WrapperGrid>
    );

    index += elementsToInsert.length;
  }

  return gridRows;
}
